import React, { useState, useEffect, useRef } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, LocationSelector, PhoneInput } from '../components/ui'
import { isValidPhone } from '../constants/phone'
import { ArrowLeft, User, Phone, Mail, Lock, Building, Upload, CheckCircle, FileText, UploadCloud } from 'lucide-react'
import { authService } from '../services/auth.service'
import { profileService } from '../services/profile.service'
import { LocationSelection } from '../components/ui/LocationSelector'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import SelfieCapture from '../components/forms/SelfieCapture'

// Mapping function to convert state IDs to state codes
const stateIdToCode: Record<string, string> = {
  'johor': 'JOH',
  'kedah': 'KED', 
  'kelantan': 'KEL',
  'kuala-lumpur': 'KL',
  'labuan': 'LAB',
  'melaka': 'MEL',
  'negeri-sembilan': 'NS',
  'pahang': 'PAH',
  'penang': 'PEN',
  'perak': 'PER',
  'perlis': 'PERL',
  'putrajaya': 'PUTRA',
  'sabah': 'SAH',
  'sarawak': 'SAW',
  'selangor': 'SEL',
  'terengganu': 'TERG'
};

const convertServiceAreas = (serviceAreas: LocationSelection): string[] => {
  // Only include states that are supported by the backend
  const supportedStateCodes = ['KL', 'SEL', 'JOH', 'PEN', 'PER', 'PAH', 'NS', 'MEL'];

  return serviceAreas.states
    .map(stateId => stateIdToCode[stateId] || stateId)
    .filter(Boolean)
    .filter(code => supportedStateCodes.includes(code));
};

// Document requirements configuration by service type
const serviceDocumentRequirements = {
  OPERATOR_TRAINING: {
    required: ['IC_PASSPORT', 'SSM_CERTIFICATE'],
    optional: [],
    descriptions: {
      IC_PASSPORT: 'IC/Passport of Authorized Personnel',
      SSM_CERTIFICATE: 'SSM/Company Incorporation Certificate'
    }
  },
  OPERATOR_OPERATION: {
    required: ['IC_PASSPORT', 'UTILITY_BILL', 'SELFIE_PHOTO'],
    optional: [],
    descriptions: {
      IC_PASSPORT: 'IC/Passport of Authorized Personnel',
      UTILITY_BILL: 'Latest Utility Bill (e.g., TNB, Air Selangor, etc.)',
      SELFIE_PHOTO: 'Selfie Photo (for ID verification and profile photo)'
    }
  },
  AMBULANCE_TRANSPORT: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION'],
    optional: ['OPERATING_PERMIT', 'VEHICLE_REGISTRATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Medical/Emergency Services Certification',
      INSURANCE_DOCUMENT: 'Vehicle and Liability Insurance',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Transport Operating Permit',
      VEHICLE_REGISTRATION: 'Ambulance Vehicle Registration Documents'
    }
  },
  GHUSL_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Ghusl Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters',
      OPERATING_PERMIT: 'Religious Services Operating Permit'
    }
  },
  KAFAN_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Kafan Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  JANAZAH_LEAD: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Janazah Leadership Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  BURIAL_PLOT: {
    required: ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION'],
    optional: ['LAND_OWNERSHIP', 'ENVIRONMENTAL_CLEARANCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Burial Services Management Certification (Optional)',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Burial Plot Operating Permit',
      LAND_OWNERSHIP: 'Land Ownership or Lease Documents',
      ENVIRONMENTAL_CLEARANCE: 'Environmental Clearance Certificate'
    }
  },
  CATERING: {
    required: ['BUSINESS_LICENSE', 'FOOD_HANDLER_CERT', 'IDENTIFICATION'],
    optional: ['HALAL_CERTIFICATION', 'KITCHEN_FACILITY_LICENSE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      FOOD_HANDLER_CERT: 'Food Handler Certificate',
      IDENTIFICATION: 'IC/Passport of Food Handlers',
      HALAL_CERTIFICATION: 'Halal Certification',
      KITCHEN_FACILITY_LICENSE: 'Kitchen Facility License',
      PROFESSIONAL_CERTIFICATE: 'Food Handler Certificate',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      OPERATING_PERMIT: 'Food Business Operating Permit'
    }
  },
  FLOWERS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Flower Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  TENT_RENTAL: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['EQUIPMENT_REGISTRATION', 'SAFETY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      EQUIPMENT_REGISTRATION: 'Equipment Registration Documents',
      SAFETY_CERTIFICATIONS: 'Safety Compliance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  PHOTOGRAPHY: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PORTFOLIO', 'PROFESSIONAL_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PORTFOLIO: 'Photography Portfolio',
      PROFESSIONAL_CERTIFICATIONS: 'Photography Professional Certifications',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)'
    }
  },
  YASIN_BOOKS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PUBLISHER_AGREEMENTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PUBLISHER_AGREEMENTS: 'Publisher Distribution Agreements',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  ISLAMIC_SOUVENIRS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  }
};

// Helper function to get document requirements for selected service type
const getDocumentRequirements = (serviceType: string) => {
  if (!serviceType || !serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements]) {
    return { required: [], optional: [], descriptions: {} };
  }
  return serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements];
};

// Helper function to get required documents for selected service type
const getRequiredDocuments = (serviceType: string) => {
  const requirements = getDocumentRequirements(serviceType);
  return requirements.required || [];
};

// Helper function to map service type to user role
const mapServiceTypeToRole = (serviceType: string) => {
  switch (serviceType) {
    // New Operator Types
    case 'OPERATOR_TRAINING':
      return 'OPERATOR_TRAINING';

    case 'OPERATOR_OPERATION':
      return 'OPERATOR_OPERATION';

    // Database ServiceType enum values
    case 'AMBULANCE_TRANSPORT':
    case 'GHUSL_SERVICE':
    case 'KAFAN_SERVICE':
      return 'AMBULANCE_PROVIDER';

    case 'JANAZAH_LEAD':
      return 'USTADZ';

    case 'BURIAL_PLOT':
      return 'CEMETERY_PROVIDER';

    case 'CATERING':
    case 'FLOWERS':
    case 'TENT_RENTAL':
    case 'PHOTOGRAPHY':
    case 'YASIN_BOOKS':
    case 'ISLAMIC_SOUVENIRS':
      return 'VENDOR';

    // Legacy mappings for backward compatibility
    case 'AMBULANCE_PROVIDER':
      return 'AMBULANCE_PROVIDER';
    case 'USTADZ':
      return 'USTADZ';
    case 'CEMETERY':
      return 'CEMETERY_PROVIDER';
    case 'FUNERAL_HOME':
      return 'PROVIDER';

    default:
      return 'PROVIDER';
  }
};

// Helper function to safely get document description
const getDocumentDescription = (serviceType: string, docType: string) => {
  const requirements = getDocumentRequirements(serviceType);
  return requirements.descriptions[docType] || docType.replace(/_/g, ' ');
};

// Profile picture limits — kept in sync with the server's
// POST /profile/picture FileInterceptor (type + size).
const MAX_PROFILE_PICTURE_BYTES = 5 * 1024 * 1024
const ALLOWED_PROFILE_PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`

interface FormData {
  // Family Registration
  familyEmail: string
  familyPassword: string
  familyFirstName: string
  familyLastName: string
  familyPhone: string
  familyNationality: string
  familyPassportNumber?: string
  familyReferralCode?: string
  familyProfilePicture?: File

  // Provider Registration
  providerEmail: string
  providerPassword: string
  providerFirstName: string
  providerLastName: string
  providerPhone: string
  serviceType: string
  businessName: string
  providerAddress?: string
  providerCity?: string
  providerState?: string
  providerPostalCode?: string
  serviceAreas: LocationSelection

  // Provider Documents
  documents: Record<string, File>
  businessLicenseFile?: File
  professionalCertificateFile?: File
  insuranceDocumentFile?: File
  identificationDocumentFile?: File
  otherDocumentFile?: File
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams()
  const [userType, setUserType] = useState<'family' | 'provider' | null>(null)
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState<FormData>({
    familyEmail: '',
    familyPassword: '',
    familyFirstName: '',
    familyLastName: '',
    familyPhone: '',
    familyNationality: '',
    familyPassportNumber: '',
    familyReferralCode: '',
    providerEmail: '',
    providerPassword: '',
    providerFirstName: '',
    providerLastName: '',
    providerPhone: '',
    serviceType: '',
    businessName: '',
    providerAddress: '',
    providerCity: '',
    providerState: '',
    providerPostalCode: '',
    serviceAreas: { states: [], cities: [] },
    documents: {}
  })

  // Check URL params for pre-selected user type
  useEffect(() => {
    const type = searchParams.get('type')
    if (type === 'provider' || type === 'family') {
      setUserType(type)
    }
  }, [searchParams])

  const handleInputChange = (field: keyof FormData, value: string | File | LocationSelection) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleDocumentUpload = (docType: string, file: File | null) => {
    if (!file) return

    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [docType]: file
      }
    }))
  }

  // Step 3 (family): optional profile picture. Validated here so the user
  // learns about a bad file immediately; the server re-checks on upload.
  const profilePictureInputRef = useRef<HTMLInputElement>(null)
  const [isProfileDragging, setIsProfileDragging] = useState(false)

  const selectProfilePicture = (file: File | null | undefined) => {
    if (!file) return
    if (!ALLOWED_PROFILE_PICTURE_TYPES.includes(file.type)) {
      toast.error('Please choose a PNG, JPG, or WebP image')
      return
    }
    if (file.size > MAX_PROFILE_PICTURE_BYTES) {
      toast.error('Profile picture must be 5MB or smaller')
      return
    }
    handleInputChange('familyProfilePicture', file)
  }

  const removeProfilePicture = () => {
    setFormData(prev => ({ ...prev, familyProfilePicture: undefined }))
    // Reset the input so re-selecting the same file fires onChange again.
    if (profilePictureInputRef.current) profilePictureInputRef.current.value = ''
  }

  const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState<string | null>(null)
  useEffect(() => {
    const file = formData.familyProfilePicture
    if (!file) {
      setProfilePicturePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setProfilePicturePreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [formData.familyProfilePicture])

  const [isLoading, setIsLoading] = useState(false)

  // Referral code (optional): live-checked against the API so the user learns
  // a code is bad here, not after completing the whole form. The registration
  // endpoint re-validates authoritatively.
  const [referralCheck, setReferralCheck] = useState<{
    status: 'idle' | 'checking' | 'valid' | 'invalid'
    reason?: string
  }>({ status: 'idle' })
  const referralDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The referral program can be switched off per environment — the field
  // hides itself when the status endpoint says it is disabled. Fails open so
  // a status-fetch hiccup never hides a working feature.
  const [referralsEnabled, setReferralsEnabled] = useState(true)
  useEffect(() => {
    authService
      .referralStatus()
      .then((r) => setReferralsEnabled(!!r.enabled))
      .catch(() => undefined)
  }, [])

  const handleReferralCodeChange = (value: string) => {
    // Codes are uppercase, no spaces — normalize as the user types.
    const normalized = value.replace(/\s+/g, '').toUpperCase()
    handleInputChange('familyReferralCode', normalized)
    if (referralDebounce.current) clearTimeout(referralDebounce.current)

    if (normalized.length !== 6) {
      setReferralCheck({ status: 'idle' })
      return
    }
    setReferralCheck({ status: 'checking' })
    referralDebounce.current = setTimeout(async () => {
      try {
        const result = await authService.validateReferralCode(normalized)
        setReferralCheck({ status: result.valid ? 'valid' : 'invalid', reason: result.reason })
      } catch {
        setReferralCheck({ status: 'idle' }) // network hiccup — let submit decide
      }
    }, 500)
  }

  const referralErrorMessage = (reason?: string) => {
    switch (reason) {
      case 'MALFORMED':
        return 'Invalid referral code format'
      case 'DISABLED':
        return 'Referrals are not active right now'
      default:
        return 'Referral code not found'
    }
  }

  const handleFamilyRegistration = async () => {
    // Step-level validation: required fields must be filled before advancing.
    if (step === 1) {
      if (!formData.familyEmail.trim()) {
        toast.error('Please enter your email address')
        return
      }
      if (!/\S+@\S+\.\S+/.test(formData.familyEmail)) {
        toast.error('Please enter a valid email address')
        return
      }
      if (!formData.familyPassword || formData.familyPassword.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }
      // Fail fast on a known-bad referral code — no point completing the form.
      if (referralCheck.status === 'invalid') {
        toast.error(referralErrorMessage(referralCheck.reason))
        return
      }
      setStep(2)
      return
    }
    if (step === 2) {
      if (!formData.familyFirstName.trim() || !formData.familyLastName.trim()) {
        toast.error('Please enter your first and last name')
        return
      }
      if (!formData.familyPhone.trim()) {
        toast.error('Please enter your phone number')
        return
      }
      if (!isValidPhone(formData.familyPhone)) {
        toast.error('Please enter a valid Malaysian or Singaporean phone number')
        return
      }
      if (!formData.familyNationality) {
        toast.error('Please select your nationality')
        return
      }
      setStep(3)
      return
    }

    // Validate required fields
    if (!formData.familyEmail || !formData.familyPassword || !formData.familyFirstName || !formData.familyLastName || !formData.familyPhone || !formData.familyNationality) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!isValidPhone(formData.familyPhone)) {
      toast.error('Please enter a valid Malaysian or Singaporean phone number')
      return
    }

    setIsLoading(true)
    try {
      const response = await authService.registerFamily({
        email: formData.familyEmail,
        password: formData.familyPassword,
        firstName: formData.familyFirstName,
        lastName: formData.familyLastName,
        phone: formData.familyPhone,
        nationality: formData.familyNationality,
        passportNumber: formData.familyPassportNumber,
        referralCode: formData.familyReferralCode?.trim() || undefined
      })

      // Store auth data
      authService.setAuthData(response.access_token, response.user)

      // Upload the optional profile picture now that we hold a token. The
      // account already exists, so an upload failure must not fail it — the
      // user can add a picture later from their profile.
      if (formData.familyProfilePicture) {
        try {
          await profileService.uploadProfilePicture(formData.familyProfilePicture)
        } catch (uploadError) {
          console.error('Profile picture upload failed:', uploadError)
          toast.error('Account created, but your profile picture could not be uploaded. You can add one later in your profile.')
        }
      }

      toast.success('Family account created successfully!')
      window.location.href = '/dashboard'
    } catch (error: any) {
      console.error('Registration error:', error)
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleProviderRegistration = async () => {
    // Step-level validation: required fields must be filled before advancing.
    if (step === 1) {
      if (!formData.providerFirstName.trim() || !formData.providerLastName.trim()) {
        toast.error('Please enter your first and last name')
        return
      }
      if (!formData.providerEmail.trim()) {
        toast.error('Please enter your email address')
        return
      }
      if (!/\S+@\S+\.\S+/.test(formData.providerEmail)) {
        toast.error('Please enter a valid email address')
        return
      }
      if (!formData.providerPhone.trim()) {
        toast.error('Please enter your phone number')
        return
      }
      if (!isValidPhone(formData.providerPhone)) {
        toast.error('Please enter a valid Malaysian or Singaporean phone number')
        return
      }
      if (!formData.providerPassword || formData.providerPassword.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }
      setStep(2)
      return
    }
    if (step === 2) {
      if (!formData.serviceType) {
        toast.error('Please select your service type')
        return
      }
      if (!formData.businessName.trim()) {
        toast.error('Please enter your business name')
        return
      }
      setStep(3)
      return
    }

    // Validate required fields
    if (!formData.providerEmail || !formData.providerPassword || !formData.providerFirstName || !formData.providerLastName || !formData.providerPhone || !formData.serviceType || !formData.businessName) {
      toast.error('Please fill in all required fields')
      return
    }

    if (!isValidPhone(formData.providerPhone)) {
      toast.error('Please enter a valid Malaysian or Singaporean phone number')
      return
    }

    if (formData.serviceAreas.states.length === 0) {
      toast.error('Please select at least one service area')
      return
    }

    // Validate required documents
    const requiredDocs = getRequiredDocuments(formData.serviceType)
    const missingDocs = requiredDocs.filter(docType => !formData.documents[docType])

    if (missingDocs.length > 0) {
      toast.error(`Please upload all required documents: ${missingDocs.join(', ')}`)
      return
    }

    setIsLoading(true)
    try {
      const userData = {
        email: formData.providerEmail,
        password: formData.providerPassword,
        firstName: formData.providerFirstName,
        lastName: formData.providerLastName,
        phone: formData.providerPhone,
        role: mapServiceTypeToRole(formData.serviceType),
        businessName: formData.businessName,
        address: formData.providerAddress,
        city: formData.providerCity,
        state: formData.providerState,
        postalCode: formData.providerPostalCode,
        serviceAreas: convertServiceAreas(formData.serviceAreas)
      }

      const response = await authService.registerProviderWithDocuments(userData, formData.documents)

      toast.success('Provider application submitted successfully! Please await verification.')
      window.location.href = '/login'
    } catch (error: any) {
      console.error('Registration error:', error)
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderUserTypeSelection = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-4">Join Akhirat</h1>
        <p className="text-lg text-neutral-600">How would you like to use our platform?</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="p-8 cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-300"
              onClick={() => setUserType('family')}>
          <CardContent className="pt-0">
            <div className="text-center">
              <User className="h-16 w-16 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Membership Account</h3>
              <p className="text-neutral-600 mb-6">
                Register as a Member to arrange funeral services and coordinate with providers
              </p>
              <div className="space-y-2 text-left bg-neutral-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Create and manage funeral cases</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Book verified service providers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Coordinate religious rites</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Handle documents and payments</span>
                </div>
              </div>
              <Button variant="islamic" className="w-full mt-6">
                Register as Member
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="p-8 cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary-300"
              onClick={() => setUserType('provider')}>
          <CardContent className="pt-0">
            <div className="text-center">
              <Building className="h-16 w-16 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Service Provider</h3>
              <p className="text-neutral-600 mb-6">
                Register your business to offer funeral services to families in need
              </p>
              <div className="space-y-2 text-left bg-neutral-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Receive verified business status</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Connect with families</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Manage bookings and schedules</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Receive payments securely</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-6">
                Continue as Provider
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )

  const renderFamilyRegistration = () => (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <User className="h-6 w-6 text-primary-600" />
              <span>Family Registration</span>
            </div>
            <p className="text-sm font-normal text-neutral-600">Step {step} of 3</p>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Account Information</h3>
                <p className="text-sm text-neutral-600">Create your account credentials</p>
              </div>

              <Input
                label="Email Address"
                type="email"
                placeholder="your@email.com"
                value={formData.familyEmail}
                onChange={(e) => handleInputChange('familyEmail', e.target.value)}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="Create a strong password"
                value={formData.familyPassword}
                onChange={(e) => handleInputChange('familyPassword', e.target.value)}
                helperText="Must be at least 8 characters long"
                required
              />

              {referralsEnabled && (
              <div>
                <Input
                  label="Referral Code"
                  placeholder="e.g. H7K2NP"
                  value={formData.familyReferralCode || ''}
                  onChange={(e) => handleReferralCodeChange(e.target.value)}
                  maxLength={6}
                />
                {referralCheck.status === 'checking' && (
                  <p className="text-xs text-neutral-500 mt-1">Checking code…</p>
                )}
                {referralCheck.status === 'valid' && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Valid referral code — you'll get 50% off wallet activation
                  </p>
                )}
                {referralCheck.status === 'invalid' && (
                  <p className="text-xs text-red-600 mt-1">{referralErrorMessage(referralCheck.reason)}</p>
                )}
                {referralCheck.status === 'idle' && (
                  <p className="text-xs text-neutral-500 mt-1">Enter referral code (optional)</p>
                )}
              </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Personal Information</h3>
                <p className="text-sm text-neutral-600">Tell us about yourself</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  placeholder="Ahmed"
                  value={formData.familyFirstName}
                  onChange={(e) => handleInputChange('familyFirstName', e.target.value)}
                  required
                />

                <Input
                  label="Last Name"
                  placeholder="Hassan"
                  value={formData.familyLastName}
                  onChange={(e) => handleInputChange('familyLastName', e.target.value)}
                  required
                />
              </div>

              <PhoneInput
                label="Phone Number"
                value={formData.familyPhone}
                onChange={(value) => handleInputChange('familyPhone', value)}
                helperText="For emergency contact and notifications"
                required
              />

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nationality <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.familyNationality}
                    onChange={(e) => handleInputChange('familyNationality', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select your nationality</option>
                    <option value="Malaysia">Malaysia</option>
                    <option value="Singapore">Singapore</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Determines your default wallet currency
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Passport Number (Optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="A12345678"
                    value={formData.familyPassportNumber}
                    onChange={(e) => handleInputChange('familyPassportNumber', e.target.value)}
                    helperText="Required for non-Malaysian citizens"
                  />
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Upload className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Profile Picture (Optional)</h3>
                <p className="text-sm text-neutral-600">Help providers recognize you</p>
              </div>

              <input
                ref={profilePictureInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  selectProfilePicture(e.target.files?.[0])
                  e.target.value = ''
                }}
              />

              {formData.familyProfilePicture ? (
                <div className="flex flex-col items-center">
                  <img
                    src={profilePicturePreviewUrl || undefined}
                    alt="Profile picture preview"
                    className="h-32 w-32 rounded-full object-cover border-4 border-primary-100"
                  />
                  <p className="mt-3 text-sm font-medium text-neutral-900 max-w-full truncate">
                    {formData.familyProfilePicture.name}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {formatFileSize(formData.familyProfilePicture.size)}
                  </p>
                  <div className="flex space-x-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => profilePictureInputRef.current?.click()}
                    >
                      Choose another
                    </Button>
                    <Button variant="ghost" size="sm" onClick={removeProfilePicture}>
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="profile-upload"
                  onClick={() => profilePictureInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsProfileDragging(true)
                  }}
                  onDragLeave={() => setIsProfileDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault()
                    setIsProfileDragging(false)
                    selectProfilePicture(e.dataTransfer.files?.[0])
                  }}
                  className={`w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isProfileDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
                  }`}
                >
                  <Upload className="h-12 w-12 text-neutral-400 mx-auto mb-2" />
                  <p className="text-sm text-neutral-600 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-neutral-500">PNG, JPG, or WebP up to 5MB</p>
                </button>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Account created successfully</li>
                  <li>• Access to family dashboard</li>
                  <li>• Can create funeral cases immediately</li>
                  <li>• 24/7 support available</li>
                </ul>
              </div>
            </>
          )}

          <div className="flex space-x-3 pt-4">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                Back
              </Button>
            )}
            <Button
              variant="islamic"
              onClick={handleFamilyRegistration}
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : step === 3 ? 'Create Account' : 'Continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const renderProviderRegistration = () => (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Building className="h-6 w-6 text-primary-600" />
              <span>Provider Registration</span>
            </div>
            <p className="text-sm font-normal text-neutral-600">Step {step} of 3</p>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Account & Contact Information</h3>
                <p className="text-sm text-neutral-600">Create your provider account and contact details</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  placeholder="John"
                  value={formData.providerFirstName}
                  onChange={(e) => handleInputChange('providerFirstName', e.target.value)}
                  required
                />

                <Input
                  label="Last Name"
                  placeholder="Doe"
                  value={formData.providerLastName}
                  onChange={(e) => handleInputChange('providerLastName', e.target.value)}
                  required
                />
              </div>

              <Input
                label="Business Email"
                type="email"
                placeholder="business@company.com"
                value={formData.providerEmail}
                onChange={(e) => handleInputChange('providerEmail', e.target.value)}
                required
              />

              <PhoneInput
                label="Phone Number"
                value={formData.providerPhone}
                onChange={(value) => handleInputChange('providerPhone', value)}
                required
              />

              <Input
                label="Password"
                type="password"
                placeholder="Create a strong password"
                value={formData.providerPassword}
                onChange={(e) => handleInputChange('providerPassword', e.target.value)}
                required
              />
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Business Information & Documents</h3>
                <p className="text-sm text-neutral-600">Tell us about your business and upload verification documents</p>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-700">Service Type</label>
                <select
                  className="mt-1 block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.serviceType}
                  onChange={(e) => handleInputChange('serviceType', e.target.value)}
                  required
                >
                  <option value="">Select service type</option>
                  {/* <option value="OPERATOR_TRAINING">Operator Training</option> */} {/* Temporarily hidden */}
                  {/* <option value="OPERATOR_OPERATION">Operator Operation (Jenazah)</option> */} {/* Temporarily hidden */}
                  <option value="AMBULANCE_TRANSPORT">Ambulance Transport Service</option>
                  {/* <option value="GHUSL_SERVICE">Ghusl Service</option> */} {/* Temporarily hidden */}
                  {/* <option value="KAFAN_SERVICE">Kafan Service</option> */} {/* Temporarily hidden */}
                  {/* <option value="JANAZAH_LEAD">Janazah Lead Service</option> */} {/* Temporarily hidden */}
                  <option value="BURIAL_PLOT">Burial Plot Services</option>
                  <option value="ISLAMIC_SOUVENIRS">Islamic Souvenirs / Gifts</option>
                  {/* <option value="CATERING">Catering Services</option> */} {/* Temporarily hidden */}
                  {/* <option value="FLOWERS">Flower Services</option> */} {/* Temporarily hidden */}
                  {/* <option value="TENT_RENTAL">Tent Rental Services</option> */} {/* Temporarily hidden */}
                  {/* <option value="PHOTOGRAPHY">Photography Services</option> */} {/* Temporarily hidden */}
                  {/* <option value="PRINTING">Printing Services</option> */} {/* Temporarily hidden */}
                </select>
              </div>

              <Input
                label="Business Name"
                placeholder="Your Business Name"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                required
              />

              <div className="border-t pt-4 mt-4">
                <h4 className="text-md font-medium text-neutral-900 mb-4">Business Address</h4>

                <Input
                  label="Street Address"
                  placeholder="123 Jalan Ampang"
                  value={formData.providerAddress || ''}
                  onChange={(e) => handleInputChange('providerAddress', e.target.value)}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Input
                    label="City"
                    placeholder="Kuala Lumpur"
                    value={formData.providerCity || ''}
                    onChange={(e) => handleInputChange('providerCity', e.target.value)}
                  />

                  <Input
                    label="State"
                    placeholder="Selangor"
                    value={formData.providerState || ''}
                    onChange={(e) => handleInputChange('providerState', e.target.value)}
                  />
                </div>

                <Input
                  label="Postal Code"
                  placeholder="50000"
                  value={formData.providerPostalCode || ''}
                  onChange={(e) => handleInputChange('providerPostalCode', e.target.value)}
                  className="mt-4"
                />
              </div>


              {/* Dynamic Document Upload */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium text-neutral-900 mb-4">Business Documents</h4>
                <p className="text-sm text-neutral-600 mb-6">Please upload the required documents for your service type. Accepted formats: PDF, JPG, PNG (Max 10MB per file)</p>

                {formData.serviceType ? (
                  <div className="space-y-6">
                    {/* Required Documents */}
                    {getDocumentRequirements(formData.serviceType).required && getDocumentRequirements(formData.serviceType).required.length > 0 && (
                      <div>
                        <h5 className="text-md font-medium text-neutral-900 mb-3 flex items-center">
                          <span className="text-red-500 mr-1">*</span> Required Documents
                        </h5>
                        <div className="space-y-3">
                          {getDocumentRequirements(formData.serviceType).required.map((docType) => (
                            <div key={docType} className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              {docType === 'SELFIE_PHOTO' ? (
                                <div>
                                  <p className="text-sm font-medium text-neutral-700 mb-3">
                                    {getDocumentDescription(formData.serviceType, docType)} *
                                  </p>
                                  <SelfieCapture
                                    onCapture={(file) => handleDocumentUpload(docType, file)}
                                    existingImage={
                                      formData.documents && formData.documents[docType]
                                        ? URL.createObjectURL(formData.documents[docType])
                                        : undefined
                                    }
                                    width={400}
                                    height={400}
                                    className="mx-auto"
                                  />
                                  {formData.documents && formData.documents[docType] && (
                                    <p className="mt-3 text-sm text-green-600 text-center">
                                      ✓ Selfie photo captured successfully
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <label className="block">
                                  <span className="text-sm font-medium text-neutral-700">
                                    {getDocumentDescription(formData.serviceType, docType)} *
                                  </span>
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleDocumentUpload(docType, e.target.files?.[0])}
                                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    required
                                  />
                                  {formData.documents && formData.documents[docType] && (
                                    <p className="mt-2 text-sm text-green-600">✓ {formData.documents[docType].name}</p>
                                  )}
                                </label>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Optional Documents */}
                    {getDocumentRequirements(formData.serviceType).optional && getDocumentRequirements(formData.serviceType).optional.length > 0 && (
                      <div>
                        <h5 className="text-md font-medium text-neutral-900 mb-3">Optional Documents</h5>
                        <div className="space-y-3">
                          {getDocumentRequirements(formData.serviceType).optional.map((docType) => (
                            <div key={docType} className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              <label className="block">
                                <span className="text-sm font-medium text-neutral-700">
                                  {getDocumentDescription(formData.serviceType, docType)}
                                  <span className="text-gray-400 ml-1">(Optional)</span>
                                </span>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleDocumentUpload(docType, e.target.files?.[0])}
                                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                />
                                {formData.documents && formData.documents[docType] && (
                                  <p className="mt-2 text-sm text-green-600">✓ {formData.documents[docType].name}</p>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      Please select a service type to view required documents
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold">Service Areas</h3>
                <p className="text-sm text-neutral-600">Select the states and cities you serve</p>
              </div>

              <LocationSelector
                label="Service Coverage Areas"
                value={formData.serviceAreas}
                onChange={(selection) => handleInputChange('serviceAreas', selection)}
                helperText="Select all states and cities where you provide your services. This helps families find you."
                required
                maxSelections={10}
                showCities={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Verification Process</h4>
                <p className="text-sm text-blue-700 mb-2">
                  After submission, our team will verify your business license and credentials within 24-48 hours.
                </p>
                <p className="text-sm text-blue-700">
                  Selected service areas will be visible to families searching for providers in those locations.
                </p>
              </div>
            </>
          )}

          <div className="flex space-x-3 pt-4">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                Back
              </Button>
            )}
            <Button
              variant="islamic"
              onClick={handleProviderRegistration}
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting Application...' : step === 3 ? 'Submit Application' : 'Continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/'}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {!userType && renderUserTypeSelection()}
        {userType === 'family' && renderFamilyRegistration()}
        {userType === 'provider' && renderProviderRegistration()}
      </div>
    </div>
  )
}
